/**
 * Main form component for breeding workflow input
 */

import React, { useState, useRef } from 'react';
import type { BreedingMode, BreedingNotes, BreedingWorkflowInput, SeedRoom } from '../types/breeding';

interface BreedingFormProps {
  seedRooms: SeedRoom[];
  defaultRoomId: number | null;
  onSubmit: (input: BreedingWorkflowInput) => void;
}

export function BreedingForm({ seedRooms, defaultRoomId, onSubmit }: BreedingFormProps) {
  const [mode, setMode] = useState<BreedingMode>('self');
  const [barcode, setBarcode] = useState('');
  const [pollenDonorBarcode, setPollenDonorBarcode] = useState('');
  const [seedCount, setSeedCount] = useState('');
  const [roomOverride, setRoomOverride] = useState<string | null>(null);
  const [strainNameOverride, setStrainNameOverride] = useState('');
  const barcodeRef = useRef<HTMLInputElement>(null);
  const resolvedRoomId = roomOverride ?? (defaultRoomId !== null ? defaultRoomId.toString() : '');

  const collectNotes = (): BreedingNotes | null => {
    const flavorIntensity = (document.querySelector('input[name="flavorIntensity"]:checked') as HTMLInputElement)?.value || '';
    const flavorChecks = Array.from(
      document.querySelectorAll('input[name="flavor"]:checked')
    ) as HTMLInputElement[];
    const budLeafRatio = (document.querySelector('input[name="budLeafRatio"]:checked') as HTMLInputElement)?.value;
    const yieldVal = (document.querySelector('input[name="yield"]:checked') as HTMLInputElement)?.value;
    const budStructure = (document.querySelector('input[name="budStructure"]:checked') as HTMLInputElement)?.value;
    const potency = (document.querySelector('input[name="potency"]:checked') as HTMLInputElement)?.value;
    const additionalNotes = (document.getElementById('additionalNotes') as HTMLTextAreaElement)?.value.trim();

    // If nothing is filled, return null
    if (
      flavorChecks.length === 0 &&
      !budLeafRatio &&
      !yieldVal &&
      !budStructure &&
      !potency &&
      !additionalNotes &&
      !flavorIntensity
    ) {
      return null;
    }

    const notes: BreedingNotes = {};

    // Collect flavor intensity (if not default "Not recorded")
    if (flavorIntensity) {
      notes.flavorIntensity = flavorIntensity as BreedingNotes['flavorIntensity'];
    }

    // Collect flavors by category
    if (flavorChecks.length > 0) {
      const flavorsByCategory: Record<string, string[]> = {};
      flavorChecks.forEach((checkbox) => {
        const [category, descriptor] = checkbox.value.split(':');
        if (!flavorsByCategory[category]) {
          flavorsByCategory[category] = [];
        }
        flavorsByCategory[category].push(descriptor);
      });

      notes.flavors = Object.entries(flavorsByCategory).map(([category, descriptors]) => ({
        category,
        descriptors,
      }));
    }

    // Collect trait selections
    if (budLeafRatio) notes.budLeafRatio = budLeafRatio as BreedingNotes['budLeafRatio'];
    if (yieldVal) notes.yield = yieldVal as BreedingNotes['yield'];
    if (budStructure) notes.budStructure = budStructure as BreedingNotes['budStructure'];
    if (potency) notes.potency = potency as BreedingNotes['potency'];
    if (additionalNotes) notes.additionalNotes = additionalNotes;

    return notes;
  };

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    const notes = collectNotes();
    onSubmit({
      mode,
      barcode,
      pollenDonorBarcode: mode === 'f1Hybrid' ? pollenDonorBarcode : undefined,
      seedCount: parseInt(seedCount, 10),
      roomId: parseInt(resolvedRoomId, 10),
      notes,
      strainNameOverride: mode === 'f1Hybrid' ? strainNameOverride : undefined,
    });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <div className="input-section">
      <div className="breeding-layout">
        {/* Left Column: Core Inputs */}
        <div className="core-inputs">
          <h2>Create Breeding Run</h2>
          <p>
            {mode === 'self'
              ? 'Enter the plant barcode and number of seeds to create:'
              : 'Enter the seed parent and pollen donor barcodes for an F1 cross:'}
          </p>

          <div className="form-group">
            <label>Breeding Mode:</label>
            <div className="segmented-control mode-control" role="radiogroup" aria-label="Breeding mode">
              <input
                type="radio"
                id="mode-self"
                name="breedingMode"
                value="self"
                checked={mode === 'self'}
                onChange={() => setMode('self')}
              />
              <label htmlFor="mode-self">Selfed line</label>
              <input
                type="radio"
                id="mode-f1"
                name="breedingMode"
                value="f1Hybrid"
                checked={mode === 'f1Hybrid'}
                onChange={() => setMode('f1Hybrid')}
              />
              <label htmlFor="mode-f1">F1 hybrid cross</label>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="barcode">
              {mode === 'self' ? 'Last 4 Digits of Plant Barcode:' : 'Seed Parent Barcode:'}
            </label>
            <input
              ref={barcodeRef}
              type="text"
              id="barcode"
              maxLength={4}
              pattern="\d{4}"
              placeholder="e.g., 7892"
              autoComplete="off"
              autoFocus
              value={barcode}
              onChange={(e) => setBarcode(e.target.value)}
              onKeyPress={handleKeyPress}
            />
            {mode === 'f1Hybrid' && (
              <span className="hint">Plant that will be reassigned temporarily to create the F1 seeds</span>
            )}
          </div>

          {mode === 'f1Hybrid' && (
            <div className="form-group">
              <label htmlFor="pollenDonorBarcode">Pollen Donor Barcode:</label>
              <input
                type="text"
                id="pollenDonorBarcode"
                maxLength={4}
                pattern="\d{4}"
                placeholder="e.g., 4321"
                autoComplete="off"
                value={pollenDonorBarcode}
                onChange={(e) => setPollenDonorBarcode(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <span className="hint">Stud or donor plant used for the F1 cross</span>
            </div>
          )}

          {mode === 'f1Hybrid' && (
            <div className="form-group">
              <label htmlFor="strainNameOverride">F1 Strain Name Override:</label>
              <input
                type="text"
                id="strainNameOverride"
                placeholder="Auto: Seed Parent x Donor F1"
                autoComplete="off"
                value={strainNameOverride}
                onChange={(e) => setStrainNameOverride(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <span className="hint">Leave blank to review the generated F1 name before creating seeds</span>
            </div>
          )}

          {mode === 'f1Hybrid' && (
            <div className="info-box compact-info">
              Parent lookup happens first. You will review and edit the proposed F1 strain name before any seeds are created.
            </div>
          )}

          <div className="form-group">
            <label htmlFor="seedCount">Number of Seeds:</label>
            <input
              type="number"
              id="seedCount"
              min="1"
              max="100000"
              placeholder="e.g., 50"
              value={seedCount}
              onChange={(e) => setSeedCount(e.target.value)}
              onKeyPress={handleKeyPress}
            />
          </div>

          <div className="form-group">
            <label htmlFor="roomId">Seed Destination Room:</label>
            <select id="roomId" value={resolvedRoomId} onChange={(e) => setRoomOverride(e.target.value)}>
              {seedRooms.length === 0 ? (
                <option value="">Loading rooms...</option>
              ) : (
                seedRooms.map((room) => (
                  <option key={room.id} value={room.id}>
                    {room.name}
                  </option>
                ))
              )}
            </select>
            <span className="hint">Select the inventory room for seed storage</span>
          </div>

          <button onClick={() => handleSubmit()} className="btn-primary btn-large">
            {mode === 'self' ? 'Start Breeding Workflow' : 'Review F1 Cross'}
          </button>
        </div>

        {/* Right Column: Breeding Notes (Optional) */}
        <div className="breeding-notes">
          <div className="notes-header">
            <h3>
              =� Breeding Notes <span className="optional">(Optional)</span>
            </h3>
          </div>

          <div className="notes-panel">
            {/* Flavor Profile */}
            <div className="notes-section">
              <label className="notes-label">🍋 Flavor Profile</label>
              <p className="notes-hint">Select flavor intensity, then descriptors</p>

              {/* Flavor Intensity Selector */}
              <div style={{ marginBottom: '16px' }}>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '0.9em',
                    color: '#4a5568',
                    fontWeight: 500,
                  }}
                >
                  Intensity:
                </label>
                <div className="segmented-control">
                  <input
                    type="radio"
                    id="intensity-not-recorded"
                    name="flavorIntensity"
                    value=""
                    defaultChecked
                  />
                  <label htmlFor="intensity-not-recorded">Not recorded</label>
                  <input type="radio" id="intensity-subtle" name="flavorIntensity" value="subtle" />
                  <label htmlFor="intensity-subtle">Subtle</label>
                  <input type="radio" id="intensity-balanced" name="flavorIntensity" value="balanced" />
                  <label htmlFor="intensity-balanced">Balanced</label>
                  <input type="radio" id="intensity-bold" name="flavorIntensity" value="bold" />
                  <label htmlFor="intensity-bold">Bold</label>
                  <input
                    type="radio"
                    id="intensity-overpowering"
                    name="flavorIntensity"
                    value="overpowering"
                  />
                  <label htmlFor="intensity-overpowering">Overpowering</label>
                </div>
              </div>

              {/* Flavor Categories */}
              <div className="flavor-category">
                <div className="category-label">Citrus & Fruity</div>
                <div className="flavor-chips">
                  <label className="flavor-chip">
                    <input type="checkbox" name="flavor" value="citrus:lemon" />
                    <span>Lemon</span>
                  </label>
                  <label className="flavor-chip">
                    <input type="checkbox" name="flavor" value="citrus:grapefruit" />
                    <span>Grapefruit</span>
                  </label>
                  <label className="flavor-chip">
                    <input type="checkbox" name="flavor" value="citrus:orange" />
                    <span>Orange</span>
                  </label>
                  <label className="flavor-chip">
                    <input type="checkbox" name="flavor" value="citrus:mango" />
                    <span>Mango</span>
                  </label>
                  <label className="flavor-chip">
                    <input type="checkbox" name="flavor" value="citrus:berry" />
                    <span>Berry</span>
                  </label>
                </div>
              </div>

              <div className="flavor-category">
                <div className="category-label">Herbal & Floral</div>
                <div className="flavor-chips">
                  <label className="flavor-chip">
                    <input type="checkbox" name="flavor" value="herbal:mint" />
                    <span>Mint</span>
                  </label>
                  <label className="flavor-chip">
                    <input type="checkbox" name="flavor" value="herbal:basil" />
                    <span>Basil</span>
                  </label>
                  <label className="flavor-chip">
                    <input type="checkbox" name="flavor" value="herbal:eucalyptus" />
                    <span>Eucalyptus</span>
                  </label>
                  <label className="flavor-chip">
                    <input type="checkbox" name="flavor" value="herbal:tea" />
                    <span>Tea</span>
                  </label>
                  <label className="flavor-chip">
                    <input type="checkbox" name="flavor" value="herbal:lavender" />
                    <span>Lavender</span>
                  </label>
                </div>
              </div>

              <div className="flavor-category">
                <div className="category-label">Earthy & Woody</div>
                <div className="flavor-chips">
                  <label className="flavor-chip">
                    <input type="checkbox" name="flavor" value="earthy:soil" />
                    <span>Soil</span>
                  </label>
                  <label className="flavor-chip">
                    <input type="checkbox" name="flavor" value="earthy:moss" />
                    <span>Moss</span>
                  </label>
                  <label className="flavor-chip">
                    <input type="checkbox" name="flavor" value="earthy:forest" />
                    <span>Forest Floor</span>
                  </label>
                  <label className="flavor-chip">
                    <input type="checkbox" name="flavor" value="earthy:pine" />
                    <span>Pine</span>
                  </label>
                  <label className="flavor-chip">
                    <input type="checkbox" name="flavor" value="earthy:cedar" />
                    <span>Cedar</span>
                  </label>
                </div>
              </div>

              <div className="flavor-category">
                <div className="category-label">Fuel & Pungent</div>
                <div className="flavor-chips">
                  <label className="flavor-chip">
                    <input type="checkbox" name="flavor" value="fuel:gas" />
                    <span>Gas</span>
                  </label>
                  <label className="flavor-chip">
                    <input type="checkbox" name="flavor" value="fuel:diesel" />
                    <span>Diesel</span>
                  </label>
                  <label className="flavor-chip">
                    <input type="checkbox" name="flavor" value="fuel:skunk" />
                    <span>Skunk</span>
                  </label>
                  <label className="flavor-chip">
                    <input type="checkbox" name="flavor" value="fuel:garlic" />
                    <span>Garlic</span>
                  </label>
                  <label className="flavor-chip">
                    <input type="checkbox" name="flavor" value="fuel:pepper" />
                    <span>Pepper</span>
                  </label>
                </div>
              </div>

              <div className="flavor-category">
                <div className="category-label">Sweet & Creamy</div>
                <div className="flavor-chips">
                  <label className="flavor-chip">
                    <input type="checkbox" name="flavor" value="sweet:candy" />
                    <span>Candy</span>
                  </label>
                  <label className="flavor-chip">
                    <input type="checkbox" name="flavor" value="sweet:honey" />
                    <span>Honey</span>
                  </label>
                  <label className="flavor-chip">
                    <input type="checkbox" name="flavor" value="sweet:sugar" />
                    <span>Sugar</span>
                  </label>
                  <label className="flavor-chip">
                    <input type="checkbox" name="flavor" value="sweet:vanilla" />
                    <span>Vanilla</span>
                  </label>
                  <label className="flavor-chip">
                    <input type="checkbox" name="flavor" value="sweet:cream" />
                    <span>Cream</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Trait Selectors */}
            <div className="notes-section">
              <label className="notes-label">
                🌿 Bud:Leaf Ratio{' '}
                <span
                  className="info-icon"
                  title="High = very little fan leaves compared to bud; Low = lots of fan leaf"
                >
                  ⓘ
                </span>
              </label>
              <div className="segmented-control">
                <input type="radio" id="budLeaf-high" name="budLeafRatio" value="high" />
                <label htmlFor="budLeaf-high">High</label>
                <input type="radio" id="budLeaf-medium" name="budLeafRatio" value="medium" />
                <label htmlFor="budLeaf-medium">Medium</label>
                <input type="radio" id="budLeaf-low" name="budLeafRatio" value="low" />
                <label htmlFor="budLeaf-low">Low</label>
              </div>
            </div>

            <div className="notes-section">
              <label className="notes-label">⚖️ Yield</label>
              <p className="notes-hint">Does this plant stack on the weight?</p>
              <div className="segmented-control">
                <input type="radio" id="yield-high" name="yield" value="high" />
                <label htmlFor="yield-high">High</label>
                <input type="radio" id="yield-medium" name="yield" value="medium" />
                <label htmlFor="yield-medium">Medium</label>
                <input type="radio" id="yield-low" name="yield" value="low" />
                <label htmlFor="yield-low">Low</label>
              </div>
            </div>

            <div className="notes-section">
              <label className="notes-label">🌸 Bud Structure</label>
              <div className="segmented-control">
                <input type="radio" id="structure-dense" name="budStructure" value="dense" />
                <label htmlFor="structure-dense">Dense</label>
                <input type="radio" id="structure-spiral" name="budStructure" value="spiral" />
                <label htmlFor="structure-spiral">Spiral</label>
                <input type="radio" id="structure-larfy" name="budStructure" value="larfy" />
                <label htmlFor="structure-larfy">Larfy</label>
              </div>
            </div>

            <div className="notes-section">
              <label className="notes-label">⚡ Potency</label>
              <div className="segmented-control">
                <input type="radio" id="potency-high" name="potency" value="high" />
                <label htmlFor="potency-high">High</label>
                <input type="radio" id="potency-medium" name="potency" value="medium" />
                <label htmlFor="potency-medium">Medium</label>
                <input type="radio" id="potency-low" name="potency" value="low" />
                <label htmlFor="potency-low">Low</label>
              </div>
            </div>

            <div className="notes-section">
              <label className="notes-label">📝 Additional Notes</label>
              <textarea id="additionalNotes" rows={3} placeholder="Any other observations..." />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
